import { createClientId } from '@/lib/client-id';

export type LoSiteBlockType =
  | 'hero'
  | 'heading'
  | 'subheading'
  | 'paragraph'
  | 'columns2'
  | 'columns3'
  | 'cta'
  | 'text'
  | 'features'
  | 'testimonials'
  | 'faq'
  | 'formations'
  | 'bundles'
  | 'products'
  | 'image'
  | 'video'
  /** Emplacement du formulaire CRM (formulaires d'acquisition). */
  | 'formFields';

export type LoSiteFeaturesVariant =
  | 'default'
  | 'stats'
  | 'pain'
  | 'steps'
  | 'pricing'
  | 'cta-band';

export interface LoSiteFeatureItem {
  title: string;
  description: string;
  /** Icône prédéfinie (remplace numéro/puce pour cet élément). */
  icon?: string;
  /** Variante pricing */
  price?: string;
  period?: string;
  featured?: boolean;
}

export interface LoSiteTestimonialItem {
  quote: string;
  name: string;
  role?: string;
}

export interface LoSiteFaqItem {
  question: string;
  answer: string;
}

export type LoSiteFeaturesListStyle = 'numbered' | 'bullet';

export interface LoSiteColumnCell {
  body: string;
}

export interface LoSiteElementStyle {
  color?: string;
  backgroundColor?: string;
  hoverBackgroundColor?: string;
  hoverColor?: string;
  fontSize?: string;
  fontWeight?: string;
  fontFamily?: string;
  textAlign?: 'left' | 'center' | 'right';
  lineHeight?: string;
}

export interface LoSiteBlock {
  id: string;
  type: LoSiteBlockType;
  title?: string;
  subtitle?: string;
  body?: string;
  text?: string;
  url?: string;
  ctaText?: string;
  ctaUrl?: string;
  imageUrl?: string;
  alt?: string;
  caption?: string;
  items?: LoSiteFeatureItem[];
  columns?: LoSiteColumnCell[];
  /** Liste d'avantages — numéros ou puces par défaut (sauf icône par élément). */
  featuresListStyle?: LoSiteFeaturesListStyle;
  /** Variante visuelle (stats, pain points, étapes, pricing, bandeau CTA). */
  featuresVariant?: LoSiteFeaturesVariant;
  /** Kicker / surtitre au-dessus du titre de section. */
  featuresKicker?: string;
  /** Texte sous la liste (ex. solution pain, note pricing). */
  featuresFooter?: string;
  testimonials?: LoSiteTestimonialItem[];
  faqItems?: LoSiteFaqItem[];
  /** Styles par élément (clé ex. hero.title, hero.cta, features.item.0.title) */
  styles?: Record<string, LoSiteElementStyle>;
  /** Hero — arrière-plan plein écran */
  heroBackgroundType?: 'none' | 'image' | 'video';
  heroBackgroundImageUrl?: string;
  heroBackgroundVideoUrl?: string;
  /** Assombrissement 0–80 */
  heroBackgroundOverlay?: number;
  heroBackgroundSize?: 'cover' | 'contain';
  heroBackgroundPosition?: 'center' | 'top' | 'bottom';
  heroVideoMuted?: boolean;
  heroVideoLoop?: boolean;
}

export const LO_SITE_BLOCK_LABELS: Record<LoSiteBlockType, string> = {
  hero: 'Bannière hero',
  heading: 'Titre',
  subheading: 'Sous-titre',
  paragraph: 'Paragraphe',
  columns2: '2 colonnes',
  columns3: '3 colonnes',
  cta: 'Bouton / lien',
  text: 'Texte riche',
  features: 'Liste d\'avantages',
  testimonials: 'Témoignages',
  faq: 'FAQ',
  formations: 'Catalogue formations',
  bundles: 'Packs formations',
  products: 'Boutique produits',
  image: 'Image',
  video: 'Vidéo',
  formFields: 'Formulaire',
};

export const LO_SITE_BLOCK_TYPES: LoSiteBlockType[] = [
  'heading',
  'subheading',
  'paragraph',
  'columns2',
  'columns3',
  'hero',
  'text',
  'features',
  'testimonials',
  'faq',
  'formations',
  'bundles',
  'products',
  'image',
  'video',
  'cta',
];

function newId(): string {
  return createClientId();
}

export function createLoSiteBlock(type: LoSiteBlockType): LoSiteBlock {
  switch (type) {
    case 'hero':
      return {
        id: newId(),
        type,
        title: 'Titre principal',
        subtitle: 'Sous-titre accrocheur',
        ctaText: 'En savoir plus',
        ctaUrl: '#formations',
      };
    case 'heading':
      return { id: newId(), type, title: 'Titre de section' };
    case 'subheading':
      return { id: newId(), type, title: 'Sous-titre' };
    case 'paragraph':
      return {
        id: newId(),
        type,
        body: 'Votre paragraphe ici. Double-cliquez pour modifier le texte.',
      };
    case 'columns2':
      return {
        id: newId(),
        type,
        columns: [
          { body: 'Contenu de la colonne 1.' },
          { body: 'Contenu de la colonne 2.' },
        ],
      };
    case 'columns3':
      return {
        id: newId(),
        type,
        columns: [
          { body: 'Colonne 1' },
          { body: 'Colonne 2' },
          { body: 'Colonne 3' },
        ],
      };
    case 'cta':
      return { id: newId(), type, text: 'Découvrir', url: '#formations' };
    case 'text':
      return { id: newId(), type, body: '<p>Votre contenu ici.</p>' };
    case 'features':
      return {
        id: newId(),
        type,
        title: 'Nos avantages',
        featuresListStyle: 'numbered',
        featuresVariant: 'default',
        items: [
          { title: 'Point 1', description: 'Description courte.' },
          { title: 'Point 2', description: 'Description courte.' },
        ],
      };
    case 'testimonials':
      return {
        id: newId(),
        type,
        title: 'Ils m\'ont fait confiance',
        testimonials: [
          { quote: 'Un accompagnement clair et efficace.', name: 'Marie L.', role: 'Cliente' },
        ],
      };
    case 'faq':
      return {
        id: newId(),
        type,
        title: 'Questions fréquentes',
        faqItems: [
          { question: 'Comment ça marche ?', answer: 'Réponse à personnaliser.' },
        ],
      };
    case 'formations':
      return { id: newId(), type, title: 'Mes formations' };
    case 'bundles':
      return { id: newId(), type, title: 'Nos packs' };
    case 'products':
      return { id: newId(), type, title: 'Boutique' };
    case 'image':
      return { id: newId(), type, imageUrl: '', alt: '', caption: '' };
    case 'video':
      return { id: newId(), type, url: '', caption: '' };
    case 'formFields':
      return { id: newId(), type };
    default:
      return { id: newId(), type: 'text', body: '' };
  }
}

export function ensureLoSiteBlockIds(raw: unknown): LoSiteBlock[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((item, index) => {
    const block = item as Partial<LoSiteBlock>;
    const type = (block.type ?? 'text') as LoSiteBlockType;
    return {
      ...createLoSiteBlock(type),
      ...block,
      id: typeof block.id === 'string' && block.id ? block.id : `block-${index}-${newId()}`,
      type,
    };
  });
}

export function moveLoSiteBlock(blocks: LoSiteBlock[], from: number, to: number): LoSiteBlock[] {
  if (from === to || from < 0 || to < 0 || from >= blocks.length || to >= blocks.length) {
    return blocks;
  }
  const next = [...blocks];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

export function updateLoSiteBlock(blocks: LoSiteBlock[], id: string, patch: Partial<LoSiteBlock>): LoSiteBlock[] {
  return blocks.map((b) => (b.id === id ? { ...b, ...patch } : b));
}

export function removeLoSiteBlock(blocks: LoSiteBlock[], id: string): LoSiteBlock[] {
  return blocks.filter((b) => b.id !== id);
}

export function insertLoSiteBlockAt(
  blocks: LoSiteBlock[],
  type: LoSiteBlockType,
  index: number,
): LoSiteBlock[] {
  const block = createLoSiteBlock(type);
  const next = [...blocks];
  next.splice(Math.max(0, Math.min(index, next.length)), 0, block);
  return next;
}

export function insertLoSiteBlocksAt(
  blocks: LoSiteBlock[],
  newBlocks: LoSiteBlock[],
  index: number,
): LoSiteBlock[] {
  if (newBlocks.length === 0) return blocks;
  const next = [...blocks];
  next.splice(Math.max(0, Math.min(index, next.length)), 0, ...newBlocks);
  return next;
}

export function duplicateLoSiteBlock(blocks: LoSiteBlock[], id: string): LoSiteBlock[] {
  const index = blocks.findIndex((b) => b.id === id);
  if (index < 0) return blocks;
  const copy = { ...blocks[index], id: newId() };
  const next = [...blocks];
  next.splice(index + 1, 0, copy);
  return next;
}

export const LO_SITE_BLOCK_DESCRIPTIONS: Record<LoSiteBlockType, string> = {
  hero: 'Grande bannière avec titre, sous-titre, bouton et médias de fond.',
  heading: 'Titre de section (H2).',
  subheading: 'Sous-titre ou intertitre.',
  paragraph: 'Bloc de texte simple, une ou plusieurs phrases.',
  columns2: 'Deux colonnes côte à côte pour structurer le contenu.',
  columns3: 'Trois colonnes pour présenter des points ou des blocs.',
  text: 'Paragraphes, titres et mise en forme riche.',
  features: 'Grille d\'avantages ou de points clés.',
  testimonials: 'Citations et avis clients.',
  faq: 'Questions / réponses en accordéon.',
  formations: 'Catalogue automatique de vos formations publiées.',
  bundles: 'Packs de formations groupés.',
  products: 'Catalogue automatique de vos produits physiques actifs.',
  image: 'Image avec légende et texte alternatif.',
  video: 'Vidéo YouTube, Vimeo ou fichier (URL / import).',
  cta: 'Bouton ou lien d\'appel à l\'action.',
  formFields: 'Emplacement des champs du formulaire (CRM). Un seul par page.',
};
